import { useEffect, useRef, useState } from 'react'
import { ArrowRight, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { AppraisalEngineHeader } from '@/components/deals/appraisal/AppraisalEngineHeader'
import { AppraisalOutputPanel } from '@/components/deals/appraisal/AppraisalOutputPanel'
import { CostAssumptionsPanel } from '@/components/deals/appraisal/CostAssumptionsPanel'
import { FinanceStructurePanel } from '@/components/deals/appraisal/FinanceStructurePanel'
import { HoldingAssumptionsPanel } from '@/components/deals/appraisal/HoldingAssumptionsPanel'
import { SchemePanel } from '@/components/deals/appraisal/SchemePanel'
import { StrategyTargetingPanel } from '@/components/deals/appraisal/StrategyTargetingPanel'
import { TransactionTaxPanel } from '@/components/deals/appraisal/TransactionTaxPanel'
import {
  CONDITIONAL_COSTS,
  isCommercialUseType,
  isCoWorkStrategy,
  isServicedAccommodationStrategy,
} from '@/components/deals/appraisal/appraisal-data'
import {
  APPRAISAL_FORMULA_VERSION,
  buildResidualAppraisalResult,
  parseNumber,
  strategyFromConcept,
} from '@/components/deals/appraisal/appraisal-calculations'
import { inferTaxJurisdiction } from '@/components/deals/appraisal/appraisal-transaction-tax'
import {
  applyBrrrSubjectComparable,
  appraisalSubjectComparable,
  buildCommercialSchemeFromIncomeSchedule,
  buildMarketAwareAppraisalStates,
  commercialSchemeMatchesIncomeSchedule,
  refreshBrrrMarketData,
  refreshCommercialMarketData,
  refreshHmoMarketData,
} from '@/components/deals/appraisal/appraisal-market-data'
import {
  createDealAppraisalSnapshot,
  getDealAppraisal,
  getDealComparables,
  getDealEnrichment,
  updateDealAppraisal,
} from '@/lib/api'
import { looksPersistedDealId } from '@/lib/persisted-deal'
import { useSession } from '@/lib/session-context'

const DEFAULT_BASE_COST_VALUES = {
  'Refurb / Build Cost': '',
  'Contingency (% of refurb/build)': '',
  'Professional Fees': '',
  'Legal Costs': '',
  'Survey Costs': '',
}

const DEFAULT_HOLDING_STATE = {
  monthlyHoldingCosts: '',
  monthsToRefinance: '',
}

const DEFAULT_FINANCE_STATE = {
  purchaseInterestRatePa: '',
  purchaseLtvPercent: '',
  purchaseBrokerFee: '',
  purchaseArrangementFeePercent: '',
  purchaseValuationFee: '',
  developmentInterestRatePa: '',
  developmentLtcPercent: '',
  developmentBrokerFee: '',
  developmentArrangementFeePercent: '',
  developmentValuationFee: '',
  refinanceInterestRatePa: '',
  refinanceLtvPercent: '',
  refinanceBrokerFee: '',
  refinanceArrangementFeePercent: '',
  refinanceValuationFee: '',
}

const COMPARABLE_PROFILE_FIELDS = new Set(['propertyType', 'bedroomCount', 'internalArea'])
const DRAFT_PERSISTED_BRRR_FIELDS = new Set([
  ...COMPARABLE_PROFILE_FIELDS,
  'valuationMethod',
])

export function DealAppraisalTab({
  deal,
  selectedStrategy,
  appraisalResults,
  marketData = {},
  defaultTargetReturn = '',
  onCalculate,
  onAppraisalSaved,
}) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal?.id))
  const selectedCurrentStrategy = strategyFromConcept(selectedStrategy)
  const isCoWork = isCoWorkStrategy(selectedStrategy)
  const isSelectedServicedAccommodation = isServicedAccommodationStrategy(selectedStrategy)
  const postcodeArea = deal?.postcodeArea
  const initialSchemeStates = buildMarketAwareAppraisalStates({
    deal: { postcodeArea },
    marketData,
    selectedStrategy,
  })
  const [strategySelection, setStrategySelection] = useState({
    source: selectedCurrentStrategy,
    value: null,
  })
  const [isServicedAccommodationSetupEnabled, setIsServicedAccommodationSetupEnabled] = useState(() => (
    isSelectedServicedAccommodation
  ))
  const calculationTimerRef = useRef(null)
  const comparableRequestRef = useRef(0)
  const draftSaveQueueRef = useRef(Promise.resolve())
  const isMountedRef = useRef(true)
  const initialTargetReturn = String(defaultTargetReturn ?? '').trim()
  const targetReturnSourceRef = useRef(initialTargetReturn ? 'settings' : 'empty')
  const [targetMetric, setTargetMetric] = useState('ROCE')
  const [targetReturn, setTargetReturn] = useState(initialTargetReturn)
  const [brrrState, setBrrrState] = useState(() => initialSchemeStates.brrr)
  const brrrStateRef = useRef(initialSchemeStates.brrr)
  const [hmoState, setHmoState] = useState(() => initialSchemeStates.hmo)
  const [commercialState, setCommercialState] = useState(() => initialSchemeStates.commercial)
  const [baseCostValues, setBaseCostValues] = useState(DEFAULT_BASE_COST_VALUES)
  const [conditionalCostValues, setConditionalCostValues] = useState({})
  const [conditionalCostToggles, setConditionalCostToggles] = useState({})
  const [holdingState, setHoldingState] = useState(DEFAULT_HOLDING_STATE)
  const [financeState, setFinanceState] = useState(DEFAULT_FINANCE_STATE)
  const [transactionTaxState, setTransactionTaxState] = useState(() => (
    buildDefaultTransactionTaxState(postcodeArea, selectedCurrentStrategy)
  ))
  const [isCalculating, setIsCalculating] = useState(false)
  const [localResult, setLocalResult] = useState(null)
  const [error, setError] = useState(null)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [loadedEnrichment, setLoadedEnrichment] = useState(null)
  const [loadedSubjectComparables, setLoadedSubjectComparables] = useState(null)
  const [incomeScheduleStale, setIncomeScheduleStale] = useState(false)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      if (calculationTimerRef.current) {
        window.clearTimeout(calculationTimerRef.current)
        calculationTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!['empty', 'settings'].includes(targetReturnSourceRef.current)) return

    const nextTargetReturn = String(defaultTargetReturn ?? '').trim()
    targetReturnSourceRef.current = nextTargetReturn ? 'settings' : 'empty'
    setTargetReturn(nextTargetReturn)
  }, [defaultTargetReturn])

  useEffect(() => {
    if (!shouldPersist) return undefined

    let ignore = false

    Promise.all([
      getDealAppraisal(deal.id, { csrfToken }),
      getDealEnrichment(deal.id, { csrfToken }).catch(() => null),
      getDealComparables(deal.id, { csrfToken }).catch(() => null),
    ])
      .then(([payload, enrichment, subjectComparables]) => {
        if (ignore) return

        const currentInputs = payload.appraisal?.currentInputs
        if (currentInputs?.strategy?.targetReturn) {
          targetReturnSourceRef.current = 'persisted'
        }
        setLoadedEnrichment(enrichment)
        setLoadedSubjectComparables(subjectComparables)
        const persistedCommercialUnits = currentInputs?.scheme?.commercial?.units
        setIncomeScheduleStale(
          Array.isArray(persistedCommercialUnits) &&
          !commercialSchemeMatchesIncomeSchedule(persistedCommercialUnits, enrichment?.units),
        )
        const schemeStates = buildMarketAwareAppraisalStates({
          currentInputs,
          deal: { postcodeArea },
          enrichment,
          marketData,
          selectedStrategy,
          subjectComparables,
        })
        brrrStateRef.current = schemeStates.brrr
        applyPersistedInputs(currentInputs, {
          selectedCurrentStrategy,
          setStrategySelection,
          setTargetMetric,
          setTargetReturn,
          setIsServicedAccommodationSetupEnabled,
          setBrrrState,
          setHmoState,
          setCommercialState,
          setBaseCostValues,
          setConditionalCostValues,
          setConditionalCostToggles,
          setHoldingState,
          setFinanceState,
          setTransactionTaxState,
        }, schemeStates, buildDefaultTransactionTaxState(postcodeArea, selectedCurrentStrategy))
        const latestOutput = payload.latestSnapshot?.outputSnapshot ?? payload.appraisal?.latestOutputs
        if (latestOutput && Object.keys(latestOutput).length > 0) setLocalResult(latestOutput)

        const requestId = comparableRequestRef.current + 1
        comparableRequestRef.current = requestId
        loadAppraisalComparable(
          deal.id,
          csrfToken,
          appraisalComparableProfile(schemeStates.brrr),
        )
          .then((appraisalComparables) => {
            if (
              ignore ||
              !appraisalComparables ||
              !isMountedRef.current ||
              requestId !== comparableRequestRef.current
            ) return

            const currentState = brrrStateRef.current
            if (
              appraisalComparableProfileKey(currentState) !==
              appraisalComparableProfileKey(schemeStates.brrr)
            ) return

            const resolvedState = applyBrrrSubjectComparable(
              currentState,
              marketData,
              postcodeArea,
              appraisalComparables,
            )
            brrrStateRef.current = resolvedState
            setBrrrState(resolvedState)
          })
          .catch(() => undefined)
      })
      .catch((loadError) => {
        if (!ignore) setError(loadError.message)
      })

    return () => { ignore = true }
  }, [
    csrfToken,
    deal?.id,
    marketData,
    postcodeArea,
    selectedCurrentStrategy,
    selectedStrategy,
    shouldPersist,
  ])

  function handleUpdateBaseCost(label, value) {
    setBaseCostValues((values) => ({ ...values, [label]: value }))
  }

  function handleToggleConditionalCost(name, isEnabled) {
    setConditionalCostToggles((toggles) => ({ ...toggles, [name]: isEnabled }))
    setConditionalCostValues((values) => ({
      ...values,
      [name]: values[name] ?? CONDITIONAL_COSTS[name]?.defaultValue ?? '',
    }))
  }

  function handleUpdateConditionalCost(name, value) {
    setConditionalCostValues((values) => ({ ...values, [name]: value }))
  }

  const isSAEnabled =
    isServicedAccommodationSetupEnabled ||
    brrrState.operatingModel === 'SA' ||
    isSelectedServicedAccommodation
  const currentStrategy = strategySelection.source === selectedCurrentStrategy
    ? strategySelection.value ?? selectedCurrentStrategy
    : selectedCurrentStrategy
  const result = localResult ?? appraisalResults
  const hasCalculated = Boolean(result)
  const calculateButtonLabel = buttonLabelForCalculation(isCalculating, hasCalculated)
  const usesCommercialScheme = currentStrategy === 'Commercial / Mixed-Use' ||
    currentStrategy === 'Commercial Conversion'

  function handleRefreshIncomeSchedule() {
    setCommercialState(buildCommercialSchemeFromIncomeSchedule({
      enrichment: loadedEnrichment,
      marketData,
      postcodeArea,
      selectedStrategy,
      subjectComparables: loadedSubjectComparables,
    }))
    setIncomeScheduleStale(false)
    setLocalResult(null)
    setShowValidationErrors(false)
  }

  const handleCalculate = () => {
    if (hasMissingMarketInputs(currentStrategy, { brrrState, hmoState, commercialState })) {
      setShowValidationErrors(true)
      return
    }

    setShowValidationErrors(false)
    if (calculationTimerRef.current) {
      window.clearTimeout(calculationTimerRef.current)
    }

    setIsCalculating(true)
    calculationTimerRef.current = window.setTimeout(() => {
      if (!isMountedRef.current) {
        calculationTimerRef.current = null
        return
      }

      calculationTimerRef.current = null
      const inputs = currentInputs()
      const nextResult = buildResidualAppraisalResult(inputs)
      persistCalculation(nextResult)
    }, 300)
  }

  const currentInputs = ({ brrrStateOverride = brrrState } = {}) => buildAppraisalInputs({
    currentStrategy,
    targetMetric,
    targetReturn,
    selectedStrategy,
    isSAEnabled,
    brrrState: brrrStateOverride,
    hmoState,
    commercialState,
    baseCostValues,
    conditionalCostValues,
    conditionalCostToggles,
    holdingState,
    financeState,
    transactionTaxState,
  })

  function persistDraftInputs(brrrStateOverride) {
    if (!shouldPersist) return

    const inputs = currentInputs({ brrrStateOverride })
    draftSaveQueueRef.current = draftSaveQueueRef.current
      .catch(() => undefined)
      .then(() => updateDealAppraisal(deal.id, {
        selectedStrategyId: selectedStrategy?.persistedId,
        currentInputs: inputs,
        activitySource: 'manual',
      }, { csrfToken }))
      .then(() => {
        if (isMountedRef.current) setError(null)
      })
      .catch((saveError) => {
        if (isMountedRef.current) setError(saveError.message)
      })
  }

  function handleBrrrStateChange(nextState, { field } = {}) {
    const refreshedState = refreshBrrrMarketData(
      brrrState,
      nextState,
      marketData,
      postcodeArea,
    )
    brrrStateRef.current = refreshedState
    setBrrrState(refreshedState)

    if (DRAFT_PERSISTED_BRRR_FIELDS.has(field)) persistDraftInputs(refreshedState)
    if (COMPARABLE_PROFILE_FIELDS.has(field)) refreshAppraisalComparable(refreshedState)
  }

  async function refreshAppraisalComparable(requestedState) {
    const requestId = comparableRequestRef.current + 1
    comparableRequestRef.current = requestId
    const profile = appraisalComparableProfile(requestedState)
    if (!profile || !shouldPersist) return

    try {
      const subjectComparables = await loadAppraisalComparable(deal.id, csrfToken, profile)
      if (!isMountedRef.current || requestId !== comparableRequestRef.current) return
      if (!subjectComparables) return

      const currentState = brrrStateRef.current
      if (appraisalComparableProfileKey(currentState) !== appraisalComparableProfileKey(requestedState)) return

      const resolvedState = applyBrrrSubjectComparable(
        currentState,
        marketData,
        postcodeArea,
        subjectComparables,
      )
      brrrStateRef.current = resolvedState
      setBrrrState(resolvedState)
      persistDraftInputs(resolvedState)
    } catch {
      // Keep the activation market baseline when a profile-specific comparable is unavailable.
    }
  }

  const persistCalculation = async (nextResult) => {
    if (!shouldPersist) {
      setLocalResult(nextResult)
      setIsCalculating(false)
      onCalculate?.(nextResult.maxPrice, nextResult.targetClose, nextResult)
      return
    }

    try {
      setError(null)
      const inputs = currentInputs()
      const payload = await createDealAppraisalSnapshot(deal.id, {
        selectedStrategyId: selectedStrategy?.persistedId,
        inputSnapshot: inputs,
        outputSnapshot: nextResult,
        assumptionSnapshot: inputs,
        strategySnapshot: selectedStrategy ?? {},
        formulaKey: nextResult.formulaKey ?? 'residual_roce',
        formulaVersion: nextResult.formulaVersion ?? APPRAISAL_FORMULA_VERSION,
        activitySource: 'system',
      }, { csrfToken })
      const persistedResult = payload.latestSnapshot?.outputSnapshot ?? nextResult
      setLocalResult(persistedResult)
      onAppraisalSaved?.({
        deal: payload.deal,
        snapshot: payload.latestSnapshot ?? payload.createdSnapshot,
        result: persistedResult,
      })
    } catch (saveError) {
      setError(saveError.message)
    } finally {
      setIsCalculating(false)
    }
  }

  return (
    <div>
      <AppraisalEngineHeader />

      {error ? (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
          {error}
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section className="space-y-5">
          <StrategyTargetingPanel
            currentStrategy={currentStrategy}
            targetMetric={targetMetric}
            targetReturn={targetReturn}
            onCurrentStrategyChange={(value) => {
              setStrategySelection({ source: selectedCurrentStrategy, value })
            }}
            onTargetMetricChange={setTargetMetric}
            onTargetReturnChange={(value) => {
              targetReturnSourceRef.current = 'user'
              setTargetReturn(value)
            }}
          />
          <TransactionTaxPanel
            postcodeArea={postcodeArea}
            state={transactionTaxState}
            onStateChange={setTransactionTaxState}
          />
          {usesCommercialScheme && incomeScheduleStale ? (
            <div className="flex flex-wrap items-center justify-between gap-4 border-y border-primary/30 bg-primary/5 px-5 py-4">
              <div>
                <p className="text-sm font-bold text-foreground">Income schedule changed</p>
                <p className="mt-1 text-xs font-medium text-muted-foreground">
                  Refresh scheme units when ready. Saved appraisal edits remain unchanged until then.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={handleRefreshIncomeSchedule}>
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                Refresh from Income Schedule
              </Button>
            </div>
          ) : null}
          <SchemePanel
            currentStrategy={currentStrategy}
            brrrState={brrrState}
            hmoState={hmoState}
            commercialState={commercialState}
            isCoWork={isCoWork}
            postcodeArea={postcodeArea}
            showValidationErrors={showValidationErrors}
            onBrrrStateChange={handleBrrrStateChange}
            onHmoStateChange={(nextState) => {
              setHmoState((previousState) => (
                refreshHmoMarketData(previousState, nextState, marketData, postcodeArea)
              ))
            }}
            onCommercialStateChange={(nextState) => {
              setCommercialState((previousState) => (
                refreshCommercialMarketData(
                  previousState,
                  nextState,
                  marketData,
                  postcodeArea,
                  loadedSubjectComparables,
                )
              ))
            }}
          />
          <CostAssumptionsPanel
            currentStrategy={currentStrategy}
            isSAEnabled={isSAEnabled}
            baseCostValues={baseCostValues}
            conditionalCostValues={conditionalCostValues}
            conditionalCostToggles={conditionalCostToggles}
            onUpdateBaseCost={handleUpdateBaseCost}
            onServicedAccommodationChange={setIsServicedAccommodationSetupEnabled}
            onToggleConditionalCost={handleToggleConditionalCost}
            onUpdateConditionalCost={handleUpdateConditionalCost}
          />
          <HoldingAssumptionsPanel
            state={holdingState}
            onStateChange={setHoldingState}
          />
          <FinanceStructurePanel
            state={financeState}
            onStateChange={setFinanceState}
          />
          <div className="pt-4">
            <p className="mb-2 text-center text-xs font-medium text-muted-foreground">
              Based on current assumptions
            </p>
            <Button
              type="button"
              disabled={isCalculating}
              onClick={handleCalculate}
              className="group h-auto w-full rounded-xl py-4 text-base font-bold shadow-md disabled:opacity-80"
            >
              {calculateButtonLabel}
              {!isCalculating ? (
                <ArrowRight
                  aria-hidden="true"
                  data-testid="appraisal-calculate-arrow"
                  className="h-5 w-5 transition-transform group-hover:translate-x-1"
                />
              ) : null}
            </Button>
          </div>
        </section>

        <div className="relative">
          <AppraisalOutputPanel
            hasCalculated={hasCalculated}
            selectedStrategy={selectedStrategy}
            result={result}
          />
        </div>
      </div>
    </div>
  )
}

function buttonLabelForCalculation(isCalculating, hasCalculated) {
  if (isCalculating) return 'Calculating...'
  if (hasCalculated) return 'Recalculate Appraisal'

  return 'Calculate Maximum Purchase Price'
}

function hasMissingMarketInputs(currentStrategy, { brrrState, hmoState, commercialState }) {
  if (currentStrategy === 'HMO') {
    if (hmoState.rooms.some((room) => isBlank(room.adoptedRent))) return true
    if (hmoState.valuationMethod === 'HMO Comparable') return isBlank(hmoState.hmoAdoptedValue)
    if (hmoState.valuationMethod === 'Bricks & Mortar (£/sqft)') return isBlank(hmoState.adoptedSqft)

    return false
  }

  if (currentStrategy === 'Commercial / Mixed-Use' || currentStrategy === 'Commercial Conversion') {
    return commercialState.units.some((unit) => {
      if (isCommercialUseType(unit.useType)) return isBlank(unit.adoptedSqftYear)
      if (unit.operatingModel === 'AST' && isBlank(unit.adoptedMonthlyRent)) return true
      if (unit.operatingModel === 'SA' && isBlank(unit.adoptedAdr)) return true
      if (unit.valuationBasis === 'Comparable') return isBlank(unit.adoptedValue)
      if (unit.valuationBasis === '£/sqft') return isBlank(unit.adoptedSqftValue)

      return false
    })
  }

  if (brrrState.valuationMethod === 'Comparable' && isBlank(brrrState.adoptedGdv)) return true
  if (brrrState.valuationMethod === '£/sqft' && isBlank(brrrState.adoptedSqft)) return true
  if (brrrState.exitMode === 'Sell') return false
  if (brrrState.operatingModel === 'AST') return isBlank(brrrState.adoptedMonthlyRent)

  return isBlank(brrrState.adoptedAdr)
}

function isBlank(value) {
  return String(value ?? '').trim() === ''
}

function appraisalComparableProfile(state) {
  const propertyType = String(state?.propertyType ?? '').trim()
  const sizeSqFt = String(state?.internalArea ?? '').replaceAll(',', '').trim()
  if (!propertyType || propertyType === 'Other' || parseNumber(sizeSqFt) <= 0) return null

  return {
    propertyType,
    bedrooms: String(state?.bedroomCount ?? '').trim(),
    sizeSqFt,
  }
}

function appraisalComparableProfileKey(state) {
  const profile = appraisalComparableProfile(state)
  if (!profile) return ''

  return [profile.propertyType, profile.bedrooms, profile.sizeSqFt].join('|')
}

async function loadAppraisalComparable(dealId, csrfToken, profile) {
  if (!profile) return null

  const subjectComparables = await getDealComparables(dealId, { csrfToken, profile })
  return appraisalSubjectComparable(subjectComparables) ? subjectComparables : null
}

function buildAppraisalInputs({
  currentStrategy,
  targetMetric,
  targetReturn,
  selectedStrategy,
  isSAEnabled,
  brrrState,
  hmoState,
  commercialState,
  baseCostValues,
  conditionalCostValues,
  conditionalCostToggles,
  holdingState,
  financeState,
  transactionTaxState,
}) {
  return {
    strategy: {
      currentStrategy,
      targetMetric,
      targetReturn,
      selectedStrategy,
    },
    scheme: {
      servicedAccommodationEnabled: isSAEnabled,
      brrr: brrrState,
      hmo: hmoState,
      commercial: commercialState,
    },
    costs: {
      baseCostValues,
      conditionalCostValues,
      conditionalCostToggles,
    },
    holding: holdingState,
    finance: financeState,
    transactionTax: transactionTaxState,
  }
}

function applyPersistedInputs(inputs, setters, schemeStates, defaultTransactionTaxState) {
  inputs ??= {}
  const strategy = inputs.strategy ?? {}
  const costs = inputs.costs ?? {}

  setters.setBrrrState(schemeStates.brrr)
  setters.setHmoState(schemeStates.hmo)
  setters.setCommercialState(schemeStates.commercial)

  if (strategy.currentStrategy) {
    setters.setStrategySelection({
      source: setters.selectedCurrentStrategy,
      value: strategy.currentStrategy,
    })
  }
  if (strategy.targetMetric) setters.setTargetMetric(strategy.targetMetric)
  if (strategy.targetReturn) setters.setTargetReturn(strategy.targetReturn)
  if (inputs.scheme?.servicedAccommodationEnabled !== undefined) {
    setters.setIsServicedAccommodationSetupEnabled(Boolean(inputs.scheme.servicedAccommodationEnabled))
  }
  if (costs.baseCostValues) setters.setBaseCostValues(costs.baseCostValues)
  if (costs.conditionalCostValues) setters.setConditionalCostValues(costs.conditionalCostValues)
  if (costs.conditionalCostToggles) setters.setConditionalCostToggles(costs.conditionalCostToggles)
  if (inputs.holding) setters.setHoldingState(inputs.holding)
  if (inputs.finance) {
    const finance = annualFinanceInputs(inputs.finance)
    setters.setFinanceState(Object.fromEntries(
      Object.keys(DEFAULT_FINANCE_STATE).map((key) => [key, finance[key] ?? '']),
    ))
  }
  setters.setTransactionTaxState({
    ...defaultTransactionTaxState,
    ...(inputs.transactionTax ?? {}),
  })
}

function annualFinanceInputs(finance) {
  return {
    ...finance,
    purchaseInterestRatePa: annualInterestRate(
      finance.purchaseInterestRatePa,
      finance.purchaseInterestRatePcm,
    ),
    developmentInterestRatePa: annualInterestRate(
      finance.developmentInterestRatePa,
      finance.developmentInterestRatePcm,
    ),
    refinanceArrangementFeePercent: finance.refinanceArrangementFeePercent ??
      finance.refinanceLenderFeePercent ??
      '',
  }
}

function annualInterestRate(annualRate, monthlyRate) {
  if (!isBlank(annualRate)) return annualRate
  if (isBlank(monthlyRate)) return ''

  return String(Number((parseNumber(monthlyRate) * 12).toFixed(4)))
}

function buildDefaultTransactionTaxState(postcodeArea, strategy) {
  const isNonResidential = strategy === 'Commercial / Mixed-Use' ||
    strategy === 'Commercial Conversion'

  return {
    jurisdiction: inferTaxJurisdiction(postcodeArea),
    propertyType: isNonResidential ? 'non_residential' : 'residential',
    additionalResidentialProperty: false,
  }
}
